import {
  AccordionItem,
  Alert,
  Heading,
  RadioInput,
  Spinner,
  Tooltip,
} from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel, XRaySettingsType } from '@mm/state/model/PromoterModel';

import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { chatWidgetTroubleshootingLastResultSelector } from '@mm/state/selectors/chatWidgetTroubleshootingSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import { ChatWidgetTroubleshootingTool } from '@mm/stateful-components';
import { CustomerChatConditionRulesEditor } from '@mm/stateful-components/CustomerChat/CustomerChatConditionRulesEditor/CustomerChatConditionRulesEditor';
import { useCustomerChatConditionsEditorPromoterState } from '@mm/stateful-components/CustomerChat/use-customer-chat-conditions-editor-state';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { CustomerChatInstallInformation } from '../../../../features/startBotPromoters/customerChat/CustomerChatEditor/CustomerChatInstallInformation/CustomerChatInstallInformation';
import { PromoterHeader } from '../../components/PromoterHeader/PromoterHeader';
import { SlideoutMenu } from '../../components/SlideoutMenu';
import { GetemailsSetupSlideout } from '../GetemailsSetupSlideout/GetemailsSetupSlideout';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import styles from './GetemailsSlideoutEditorNew.module.css';

export type GetemailsSlideoutEditorNewProps = {
  promoterId: number;
};

// tslint:disable-next-line:cyclomatic-complexity
export const GetemailsSlideoutEditorNewSettings = ({
  promoterId,
}: GetemailsSlideoutEditorNewProps) => {
  const hasXRaySubscriptionFlag = useSelector(
    combinedFeatureFlagsSelector,
  ).xray;
  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const promoters = useSelector(state =>
    selectPromotersByType(state, 'getemails'),
  );

  const showLeadTypesOptions = true;

  const canEditConditions = useSelector(
    combinedFeatureFlagsSelector,
  ).customerChatEditConditions;
  const promoter = promoters.find(p => p.id === promoterId);
  const promoterCRUDActions = usePromoterCRUDActions();
  const connectionCRUDActions = useConnectionCRUDActions();

  const isPromoterCreatedLessThan10MinutesAgo = promoter?.createdAt
    ? Date.now() - new Date(promoter.createdAt).getTime() < 600000
    : false;

  const { leadType } = promoter?.settings?.xray || {};

  const { setRef } = useSlideoutOnClose();
  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const {
    localPromoter,
    setLocalPromoter,
    updatePromoter,
    updateLocalPromoterField,
  } = useCustomerChatConditionsEditorPromoterState(promoter);

  const promoterHasNoConditions = (promoter: PromoterModel) =>
    !promoter.conditions?.rules.length ||
    promoter.conditions.rules.every(r => r.operator === '*');

  const warnAboutMultipleRuleFreePromoters =
    promoter &&
    promoterHasNoConditions(promoter) &&
    promoters.some(p => p.id !== promoter.id && promoterHasNoConditions(p));

  const duplicateRules =
    !warnAboutMultipleRuleFreePromoters && promoter?.conditions?.rules.length
      ? promoter.conditions.rules.filter(rule =>
          promoters.some(
            p =>
              p.id !== promoter.id &&
              (p.conditions?.rules || []).some(
                otherRule =>
                  otherRule.operator === rule.operator &&
                  otherRule.value === rule.value,
              ),
          ),
        )
      : [];

  const onPromoterChange = async (
    key: string,
    value: any,
    onCatch?: () => void,
    props?: Partial<PromoterModel>,
  ) => {
    try {
      await promoterCRUDActions.update(chatbotId, {
        id: promoter?.id,
        [key]: value,
        ...props,
      });
      successToast('Your changes have been saved.', 'top-left');
    } catch (e) {
      onCatch?.();
      errorToast(
        isShopify && e.includes('credits') ? e : 'Something went wrong',
        'top-left',
      );
    }
  };

  const onLeadsTypeUpdate = async (leadType: XRaySettingsType) => {
    if (!showLeadTypesOptions) {
      return;
    }
    const updatedSettings = {
      settings: {
        xray: {
          leadType,
        },
      },
    };
    try {
      await promoterCRUDActions.update(chatbotId, {
        id: promoter?.id,
        ...updatedSettings,
      });
      successToast('Your changes have been saved.', 'top-left');
    } catch (e) {
      errorToast('Something went wrong', 'top-left');
    }
  };

  const customActivateAction = async () => {
    if (!hasXRaySubscriptionFlag) {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['STARTBOTS-X_RAY_ACTIVATION-EDITOR'],
        feature: 'xray',
        onSuccessBilling: () => onPromoterChange('active', true),
      });
    }

    if (hasXRaySubscriptionFlag && !promoter?.active) {
      await onPromoterChange('active', true);
    } else {
      await onPromoterChange('active', false);
    }
  };

  async function fetchItem() {
    await promoterCRUDActions.retrievePromoterById(chatbotId, promoterId);
  }

  async function fetchItems() {
    await promoterCRUDActions.retrieveAll(chatbotId, 'getemails');
  }

  useEffect(() => {
    fetchItem();
    connectionCRUDActions.retrieveAll(facebookPageId, { type: 'all' });

    return () => {
      fetchItems();
    };
  }, [chatbotId]);

  const [stateName, setStateName] = useState(promoter?.name || '');

  const advancedOptionsDisabled = false;
  const onNameInputBlur = async (val = '') => {
    setStateName(val);
    const newName = val.trim();

    if (newName && newName !== promoter?.name) {
      await onPromoterChange('name', newName);
    }
  };

  const promoterHasNoNameOrDefaultName =
    !stateName.trim() ||
    (stateName.includes('Untitled Website Visitor Email Detector') &&
      isPromoterCreatedLessThan10MinutesAgo);

  const renderLeadOptionTypes = (
    <AccordionItem
      className={classNames(
        styles.box,
        styles.accordion,
        styles.accordionNested,
      )}
      childrenWrapperClassName={styles.accordionContent}
      closeOnOutsideClick={false}
      withBorder={false}
      caretStyleIcon
      title={
        <div className={styles.featureHeader}>
          <Heading text="Type of contact data" isBold size={5} />
        </div>
      }
    >
      <div
        className={classNames(styles.feature, {
          [styles.disabled]: advancedOptionsDisabled,
        })}
        style={{ gap: '4px' }}
      >
        <RadioInput
          label="Personal emails"
          name="consumer"
          value="consumer"
          disabled={advancedOptionsDisabled}
          onChange={() => onLeadsTypeUpdate('consumer')}
          checked={!leadType || leadType === 'consumer'}
        />
        {[
          ['business', 'Business emails'],
          ['business_and_consumer', 'Business & Personal emails'],
        ].map(([key, title]) => (
          <Tooltip
            key={key}
            anchorElement={
              <RadioInput
                label={title}
                name={key}
                value={key}
                disabled={advancedOptionsDisabled}
                onChange={() => onLeadsTypeUpdate(key as XRaySettingsType)}
                checked={leadType === key}
              />
            }
            css={{
              content: styles.tooltip,
              title: styles.tooltipContent,
            }}
            title="Upgrade your plan to use this feature"
            position="right"
            toolTipDisabled
            withoutLine
            noDelayClose
            useHover
          />
        ))}
      </div>
    </AccordionItem>
  );

  const isShopify = useSelector(selectIsShopifyContext);

  if (!promoter) {
    return null;
  }

  return (
    <SlideoutMenu className={styles.menu} setRef={setRef}>
      <PromoterHeader
        className={styles.header}
        headerContentClassName={styles.headerContent}
        promoter={promoter}
        title={promoter.name || ''}
        placeholder="X-Ray Pixel trigger name"
        customActivateAction={customActivateAction}
        deleteWithPromoterCRUDAction
        onNameSave={onNameInputBlur}
      />
      <SlideoutMenu.Content className={styles.content}>
        <section className={styles.section}>
          <div className={styles.box}>
            <div className={styles.boxBlock} style={{ gap: '16px' }}>
              {promoterHasNoNameOrDefaultName && (
                <Alert
                  type="warning"
                  showCloseIcon={false}
                  className={styles.warning}
                >
                  Please name new X-Ray Trigger to continue
                </Alert>
              )}

              <CustomerChatConditionRulesEditor
                usedInSlideout
                updateLocalPromoterField={updateLocalPromoterField}
                updatePromoter={updatePromoter}
                onPromoterChange={onPromoterChange}
                localPromoter={localPromoter}
                setLocalPromoter={setLocalPromoter}
                canEditConditions={canEditConditions}
                ruleTitle={
                  <Heading size={5} isBold className={styles.boxHeading}>
                    Capture leads when website visitors meet the following
                    conditions:
                  </Heading>
                }
                urlTitle={
                  <Heading
                    size={5}
                    className={styles.boxHeading}
                    isBold={false}
                  >
                    Page URL
                  </Heading>
                }
                warnAboutMultipleRuleFreePromoters={
                  warnAboutMultipleRuleFreePromoters
                }
                duplicateRules={duplicateRules}
              />

              {showLeadTypesOptions && renderLeadOptionTypes}
            </div>
          </div>
        </section>

        <section className={styles.section}>
          <AccordionItem
            className={classNames(styles.box, styles.accordion)}
            childrenWrapperClassName={styles.accordionContent}
            closeOnOutsideClick={false}
            withBorder={false}
            caretStyleIcon
            title={
              <div className={styles.featureHeader}>
                <Heading text="Install Website Script" isBold size={4} />
              </div>
            }
          >
            <CustomerChatInstallInformation
              promoter={promoter}
              usedInSlideout
              hideWhitelist
            />
          </AccordionItem>
        </section>

        <section className={styles.section}>
          <AccordionItem
            className={classNames(styles.box, styles.accordion)}
            childrenWrapperClassName={styles.accordionContent}
            closeOnOutsideClick={false}
            withBorder={false}
            caretStyleIcon
            title={
              <div className={styles.featureHeader}>
                <Heading
                  text="Verify the script is working properly"
                  isBold
                  size={4}
                />
              </div>
            }
          >
            <ChatWidgetTroubleshootingTool
              css={{
                container: styles.troubleshootContainer,
                textInput: styles.troubleshootInput,
                button: styles.troubleshootButton,
              }}
              promoter={promoter}
              buttonName="Test it"
              usedInSlideout
              isXRay
            />
          </AccordionItem>
        </section>
      </SlideoutMenu.Content>
    </SlideoutMenu>
  );
};

type SlideoutState =
  | 'loading' // Need to fetch FB page from BE to check onceSuccessfullyLoadedGlobalJs
  | 'setup' // Show Global JS setup slideout
  | 'settings'; // Show Xray slideout editor

export const GetemailsSlideoutEditorNew = props => {
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const result = useSelector(chatWidgetTroubleshootingLastResultSelector);

  const connectedPageCRUDActions = useConnectedPageCRUDActions();

  const [slideoutState, setSlideoutState] = useState<SlideoutState>(
    (() => {
      // If we already know that we've loaded successfully, then we can skip straight to settings
      if (
        activeFacebookPage?.onceSuccessfullyLoadedGlobalJs ||
        result?.onceSuccessfullyLoadedGlobalJs
      ) {
        return 'settings';
      }

      // Loading only happens if we need to fetch a fresh copy of the FB page from the BE to check latest attrs
      return 'loading';
    })(),
  );

  // Refresh the FB page from the BE for the latest onceSuccessfullyLoadedGlobalJs value
  // Runs if either: 1. we just opened the slideout (in "loading" state), or 2. if we just finished a successful test
  useEffect(() => {
    if (
      (slideoutState === 'loading' ||
        (result?.onceSuccessfullyLoadedGlobalJs &&
          !activeFacebookPage?.onceSuccessfullyLoadedGlobalJs)) &&
      activeFacebookPage?.id
    ) {
      (async () => {
        try {
          const page = await connectedPageCRUDActions.retrievePage(
            activeFacebookPage?.id,
          );

          setSlideoutState(prevState =>
            prevState === 'loading'
              ? page?.onceSuccessfullyLoadedGlobalJs || page?.getemailsActivated
                ? 'settings'
                : 'setup'
              : prevState,
          );
        } catch (e) {
          setSlideoutState(prevState =>
            prevState === 'loading' ? 'settings' : prevState,
          );
        }
      })();
    }
  }, [result?.onceSuccessfullyLoadedGlobalJs]);

  switch (slideoutState) {
    case 'loading':
      return <Spinner standalone />;
    case 'setup':
      return <GetemailsSetupSlideout {...props} />;
    case 'settings':
      return <GetemailsSlideoutEditorNewSettings {...props} />;
  }
};
